import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BodyText,
  Box,
  Card,
  ElementContainer,
  SideDrawer,
  TextField,
  withStyles,
  type AccountUserRole,
  type PermissionsMap,
} from 'vopay';
import { Button, UtilButton } from '#shared/components/UI';
import { useAlertDialog } from '#shared/providers/AlertDialog';
import { useToast } from '#shared/providers/Toast';
import useUsers from '../useUsers';
import PermissionsSearchField from '../UserPermissionsDrawer/PermissionsSearchField';
import UserPermissionsForm, {
  PermissionFormValues,
  UserPermissionsFormRef,
} from '../UserPermissionsDrawer/UserPermissionsForm';
import RoleCheckbox from './RoleCheckbox';
import useEditRoleConfirmations from './useEditRoleConfirmations';
import useRolePermissionsCatalog from './useRolePermissionsCatalog';
import useUpdateRole from './useUpdateRole';
import styles from './EditRoleDrawer.module.scss';

const cn = withStyles(styles);

const EDIT_ROLE_FORM_ID = 'editRolePermissionsForm';

type EditRoleDrawerProps = {
  isOpen: boolean;
  role: AccountUserRole | null;
  onClose: () => void;
  onRoleUpdated?: () => void;
};

export default function EditRoleDrawer({ isOpen, role, onClose, onRoleUpdated }: EditRoleDrawerProps) {
  const { account, countSubAccounts, updateRoleInSubAccounts, createRoleInSubAccounts } = useUsers();
  const { t } = useTranslation('translations', { keyPrefix: 'Shared.EditRoleDrawer' });
  const { t: tButtons } = useTranslation('translations', { keyPrefix: 'Shared.Buttons' });
  const { t: tErrors } = useTranslation('translations', { keyPrefix: 'Shared.Errors' });
  const alertDialog = useAlertDialog();
  const toast = useToast();
  const { updateRole } = useUpdateRole();

  const { confirmLocalOnlyEdit, confirmStopApplying, confirmUpdateAcrossSubAccounts, confirmApplyToAll } =
    useEditRoleConfirmations();

  const { catalog, isFetching: isFetchingCatalog, fetchCatalog } = useRolePermissionsCatalog();
  const formRef = useRef<UserPermissionsFormRef | null>(null);
  const [roleName, setRoleName] = useState('');
  const [isApplyingToAllSubaccounts, setIsApplyingToAllSubaccounts] = useState(false);
  const [hasAtLeastOnePermission, setHasAtLeastOnePermission] = useState(false);
  const [permissionSearchTerm, setPermissionSearchTerm] = useState('');
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen || !role) return;

    setRoleName(role.Name);
    setIsApplyingToAllSubaccounts(role.ApplyToAllSubaccounts);
    // The form reports validity once it mounts; until then the role's existing permissions count.
    setHasAtLeastOnePermission(role.Permissions.length > 0);
    setPermissionSearchTerm('');
  }, [isOpen, role]);

  const hasCatalog = Object.keys(catalog).length > 0;

  useEffect(() => {
    if (!isOpen || hasCatalog) return;

    // unwrap has already logged; the drawer just stays on its loading state.
    fetchCatalog().catch(() => {});
  }, [isOpen, hasCatalog]);

  // The catalog with this role's existing permissions pre-checked.
  const prefilledCatalog = useMemo(() => {
    if (!role) return {};

    const owned = new Set(role.Permissions);
    const merged: PermissionsMap = {};

    Object.entries(catalog).forEach(([groupName, group]) => {
      merged[groupName] = {};

      Object.entries(group).forEach(([permissionKey, item]) => {
        merged[groupName][permissionKey] = {
          label: item.label,
          tooltip: item.tooltip,
          permitted: owned.has(permissionKey),
        };
      });
    });

    return merged;
  }, [catalog, role]);

  async function handleSave(values: PermissionFormValues) {
    if (!role) return;

    const selectedPermissionKeys = Object.entries(values)
      .filter(([, isSelected]) => isSelected)
      .map(([permissionKey]) => permissionKey);

    const trimmedName = roleName.trim();

    const payload = {
      Name: trimmedName,
      Permissions: selectedPermissionKeys,
      ApplyToAllSubaccounts: isApplyingToAllSubaccounts,
    };

    const { hasSubAccounts } = account;
    const wasApplyingToAll = role.ApplyToAllSubaccounts;
    const hasNameChanged = trimmedName !== role.Name;
    const originalPermissions = new Set(role.Permissions);

    const havePermissionsChanged =
      selectedPermissionKeys.length !== originalPermissions.size ||
      selectedPermissionKeys.some((permissionKey) => !originalPermissions.has(permissionKey));

    const change = { role, newName: trimmedName, hasNameChanged };

    const isOffToOn = isApplyingToAllSubaccounts && hasSubAccounts && !wasApplyingToAll;
    const isOnToOff = !isApplyingToAllSubaccounts && hasSubAccounts && wasApplyingToAll;
    const isOffToOffWithSubaccounts = !isApplyingToAllSubaccounts && !wasApplyingToAll && hasSubAccounts;

    const isEditWhileApplyingToAll =
      isApplyingToAllSubaccounts && hasSubAccounts && wasApplyingToAll && (hasNameChanged || havePermissionsChanged);

    // The dialogs say how many sub-accounts are affected, so count them once, and only when one needs it.
    async function getNumSubAccounts() {
      try {
        return (await countSubAccounts?.()) ?? 0;
      } catch {
        // unwrap has already logged; the dialog just shows 0.
        return 0;
      }
    }

    // With nothing changed on a flag that stayed off, the save goes through without a dialog.
    if (isOffToOffWithSubaccounts && (hasNameChanged || havePermissionsChanged)) {
      if (!(await confirmLocalOnlyEdit(change))) return;
    }

    if (isOnToOff) {
      if (!(await confirmStopApplying({ ...change, numSubAccounts: await getNumSubAccounts() }))) return;
    }

    if (isEditWhileApplyingToAll) {
      if (!(await confirmUpdateAcrossSubAccounts({ ...change, numSubAccounts: await getNumSubAccounts() }))) return;
    }

    if (isOffToOn) {
      if (!(await confirmApplyToAll(change))) return;
    }

    setIsSubmitting(true);

    try {
      await updateRole({ RoleID: role.RoleID, ...payload });

      // The backend doesn't propagate edits to sub-accounts, so they are fanned out here: the flag already
      // on updates each sub-account's copy, off → on creates the role fresh in each.
      if (isApplyingToAllSubaccounts && hasSubAccounts && wasApplyingToAll && updateRoleInSubAccounts) {
        // Sub-account copies haven't been renamed yet, so they are found by the role's original name.
        const result = await updateRoleInSubAccounts(payload, { lookupName: role.Name });

        if (result.skipped.length > 0 || result.failed.length > 0) {
          await alertDialog.success(
            t('updateSummaryTitle', { defaultValue: 'Role Updated' }),
            <div>
              <p>
                {t('updateSummaryBody', {
                  defaultValue: 'The role has been updated on {{numUpdated}} of {{numTotal}} sub-accounts.',
                  numUpdated: result.numUpdated,
                  numTotal: result.numTotal,
                })}
              </p>

              {result.skipped.length > 0 && (
                <>
                  <p>
                    {t('updateSkippedHeading', { defaultValue: 'Role not updated on the following sub-accounts:' })}
                  </p>

                  <ul>
                    {result.skipped.map((name) => (
                      <li key={`skipped-${name}`}>{name}</li>
                    ))}
                  </ul>
                </>
              )}

              {result.failed.length > 0 && (
                <>
                  <p>{t('updateFailedHeading', { defaultValue: 'Update failed on the following sub-accounts:' })}</p>

                  <ul>
                    {result.failed.map((name) => (
                      <li key={`failed-${name}`}>{name}</li>
                    ))}
                  </ul>
                </>
              )}
            </div>,
          );
        } else {
          toast.success(
            t('subaccountPropagationSuccess', {
              defaultValue: 'Role "{{roleName}}" was updated on {{numTotal}} sub-account(s).',
              roleName: trimmedName,
              numTotal: result.numTotal,
            }),
          );
        }
      } else if (isApplyingToAllSubaccounts && hasSubAccounts && !wasApplyingToAll && createRoleInSubAccounts) {
        const result = await createRoleInSubAccounts(payload);

        if (result.failed.length > 0) {
          toast.neutral(
            t('partialSubaccountCreate', {
              defaultValue:
                'Role "{{roleName}}" updated. Applied to {{numCreated}} of {{numTotal}} sub-accounts ({{numFailed}} failed).',
              roleName: trimmedName,
              numCreated: result.numCreated,
              numTotal: result.numTotal,
              numFailed: result.failed.length,
            }),
          );
        } else {
          toast.success(
            t('subaccountCreateSuccess', {
              defaultValue: 'Role "{{roleName}}" updated and applied to {{numTotal}} sub-account(s).',
              roleName: trimmedName,
              numTotal: result.numTotal,
            }),
          );
        }
      } else {
        toast.success(
          t('successMessage', { defaultValue: 'Role "{{roleName}}" was updated successfully.', roleName: trimmedName }),
        );
      }

      onRoleUpdated?.();
      onClose();
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : tErrors('unknownError', { defaultValue: 'Unknown error' });

      alertDialog.error(t('errorTitle', { defaultValue: 'Could not update role' }), errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <SideDrawer
      isOpen={isOpen}
      onClose={onClose}
      title={t('title', { defaultValue: 'Edit Role' })}
      headerFullWidthContent={
        <div className={cn('headerSearchRow')}>
          <PermissionsSearchField
            name="editRolePermissionSearch"
            value={permissionSearchTerm}
            onChange={setPermissionSearchTerm}
            placeholder={t('searchPlaceholder', { defaultValue: 'Search Permissions' })}
          />

          <UtilButton
            size="medium"
            onClick={() => formRef.current?.toggleAll(!isAllSelected)}
            className={cn('toggleAllButton')}
          >
            {isAllSelected
              ? t('disableAll', { defaultValue: 'Disable All' })
              : t('enableAll', { defaultValue: 'Enable All' })}
          </UtilButton>
        </div>
      }
      footer={
        <Box flx="col" gapSize="12px" className={cn('footer')}>
          {account.hasSubAccounts && (
            <RoleCheckbox
              name="editApplyToAllSubaccounts"
              checked={isApplyingToAllSubaccounts}
              onChange={setIsApplyingToAllSubaccounts}
              label={
                <span className={cn('applyToSubaccountsLabel')}>
                  {t('applyToAllSubaccounts', {
                    defaultValue: 'Apply this role to all existing and future Sub-Accounts',
                  })}
                </span>
              }
            />
          )}

          <Box gapSize="12px" className={cn('footerButtons')}>
            <Button colorScheme="secondary" onClick={onClose}>
              {tButtons('cancel', { defaultValue: 'Cancel' })}
            </Button>

            <Button
              colorScheme="primary"
              disabled={!roleName.trim() || !hasAtLeastOnePermission || isFetchingCatalog || isSubmitting}
              form={EDIT_ROLE_FORM_ID}
              type="submit"
            >
              {tButtons('confirm', { defaultValue: 'Confirm' })}
            </Button>
          </Box>
        </Box>
      }
    >
      <div className={cn('body')}>
        <Card hoverEffect={false}>
          <ElementContainer
            label={t('roleName', { defaultValue: 'Role name' })}
            required
            htmlFor="editRoleName"
            padding="0 0 6px"
            input={
              <TextField
                name="editRoleName"
                placeholder={t('roleNamePlaceholder', { defaultValue: 'e.g. Admin' })}
                value={roleName}
                onChange={(e) => setRoleName(e.target.value)}
                variant="outlineShadow"
              />
            }
          />
        </Card>

        {!isFetchingCatalog && Object.keys(prefilledCatalog).length > 0 && (
          <UserPermissionsForm
            ref={formRef}
            permissions={prefilledCatalog}
            id={EDIT_ROLE_FORM_ID}
            searchTerm={permissionSearchTerm}
            onSubmit={handleSave}
            onFormStateChange={(_hasChanges, isValid) => setHasAtLeastOnePermission(isValid)}
            onAllEnabledChange={setIsAllSelected}
          />
        )}

        {isFetchingCatalog && (
          <BodyText weight="md">{t('loadingPermissions', { defaultValue: 'Loading permissions...' })}</BodyText>
        )}
      </div>
    </SideDrawer>
  );
}
